import { observer } from 'mobx-react';
import { FC, useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';

import { Flex, ProgressBar } from '@servicetitan/anvil2';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { getQueryClient } from '@servicetitan/tanstack-query-mobx';

import { AgentChatPage } from './agent-chat/components/agent-chat-page';
import { CalculatorPage } from './calculator/components/calculator-page';
import { AppointmentsApi } from './common/api/appointments.api';
import { BattlecardApi } from './common/api/battlecard.api';
import { ChatApi } from './common/api/chat.api';
import { EquipmentsApi } from './common/api/equipments.api';
import { EstimatesApi } from './common/api/estimates.api';
import { FeedbackApi } from './common/api/feedback.api';
import { HttpClient } from './common/api/http-client';
import { InvoicesApi } from './common/api/invoices.api';
import { JobDetailsApi } from './common/api/job-details.api';
import { JobsApi } from './common/api/jobs.api';
import { PartFindApi } from './common/api/part-find.api';
import { QuestionsApi } from './common/api/questions.api';
import { TechniciansApi } from './common/api/technicians.api';
import { TopicsApi } from './common/api/topics.api';
import { BattlecardStore } from './common/stores/battlecard.store';
import { ChatStore } from './common/stores/chat.store';
import { MainStore } from './common/stores/main.store';
import { EquipmentPickerPage } from './equipment-picker/components/equipment-picker-page';
import { EquipmentManualSelectStore } from './equipment-picker/store/equipment-manual-select.store';
import { EquipmentPickerStore } from './equipment-picker/store/equipment-picker.store';
import { InfoPage } from './info/components/info-page';
import { QuestionsPage } from './question-picker/components/question-picker-page';
import { ReplacementPartFinder } from './replacement-parts/components/replacement-part-finder';
import { TopicPickerPage } from './topic-picker/components/topic-picker-page';

export const AppRoutes: FC = provide({
    singletons: [
        PartFindApi,
        MainStore,
        AppointmentsApi,
        BattlecardStore,
        BattlecardApi,
        JobDetailsApi,
        TechniciansApi,
        InvoicesApi,
        JobsApi,
        HttpClient,
        TopicsApi,
        EquipmentsApi,
        QuestionsApi,
        ChatApi,
        FeedbackApi,
        EstimatesApi,
        ChatStore,
        EquipmentPickerStore,
        EquipmentManualSelectStore,
        getQueryClient(),
    ],
})(
    observer(() => {
        const [isInit, setIsInit] = useState<boolean>(false);
        const [mainStore] = useDependencies<[MainStore]>(MainStore);

        const location = useLocation();

        useEffect(() => {
            let stillHere = true;
            const init = async () => {
                const [, appointmentId, topicId, equipmentId] = location.pathname.split('/');
                /*
                 * Agent deep links (/topic/:topicSlug/...) are fully isolated from the job/topic
                 * flow — skip MainStore init but still flip isInit so the page renders.
                 */
                if (appointmentId !== 'topic') {
                    await Promise.all([
                        mainStore.initByParams(appointmentId, topicId, equipmentId),
                    ]);
                }
                if (stillHere) {
                    setIsInit(true);
                }
            };

            init();

            return () => {
                stillHere = false;
            };
        }, [mainStore, location.pathname]);

        return isInit ? (
            <Switch>
                <Route
                    path="/topic/:topicSlug/:entry/:entryId?/questions/:promptToken?"
                    component={AgentChatPage}
                />
                <Route
                    path={['/:jobId/topic-picker', '/:jobId/equipments/:equipmentId/topic-picker']}
                    component={TopicPickerPage}
                />
                <Route path={['/:jobId/calculator']} component={CalculatorPage} />
                <Route
                    path={['/:jobId/replacement-part-finder']}
                    component={ReplacementPartFinder}
                />
                <Route path="/:jobId/:topicId/equipment-picker" component={EquipmentPickerPage} />
                <Route
                    path={['/:jobId/:topicId/questions', '/:jobId/:topicId/:equipmentId/questions']}
                    component={QuestionsPage}
                />
                <Route path="/readme" component={InfoPage} />
                <Route path="/">
                    <div>Page not found!</div>
                </Route>
            </Switch>
        ) : (
            <Flex grow={1}>
                <ProgressBar
                    className="m-4"
                    label=""
                    aria-label="Loading, please wait"
                    indeterminate
                />
            </Flex>
        );
    })
);
